import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface PlanFeatures {
  chatbot?: boolean;
  pdf_advanced?: boolean;
  trial_days?: number;
  clinic_dashboard?: boolean;
}

export function planHasFeature(features: PlanFeatures | null | undefined, feature: keyof PlanFeatures): boolean {
  if (!features) return false;
  return !!features[feature];
}

export function useBusinessModel() {
  const { user } = useAuth();
  const [version, setVersion] = useState<'v1' | 'v2'>('v1');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      let effectiveVersion: 'v1' | 'v2' = 'v1';

      if (user) {
        // 1. Check per-user override
        const { data: profile } = await supabase
          .from('profiles')
          .select('business_model_override')
          .eq('id', user.id)
          .maybeSingle();

        if (profile?.business_model_override === 'v2') {
          setVersion('v2');
          setLoading(false);
          return;
        }
        if (profile?.business_model_override === 'v1') {
          setVersion('v1');
          setLoading(false);
          return;
        }

        // 2. Infer from plan slug (if user has a V2 plan)
        const { data: sub } = await supabase
          .from('subscriptions')
          .select('plan_id, plans(slug)')
          .eq('owner_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        const planSlug = (sub?.plans as any)?.slug as string | undefined;
        if (planSlug?.endsWith('_v2')) {
          setVersion('v2');
          setLoading(false);
          return;
        }
      }

      // 3. Fallback to global setting
      const { data } = await supabase
        .from('ai_settings')
        .select('value')
        .eq('key', 'business_model_version')
        .maybeSingle();

      if (data?.value === 'v2') {
        effectiveVersion = 'v2';
      }

      setVersion(effectiveVersion);
      setLoading(false);
    }
    fetch();
  }, [user]);

  return {
    version,
    isV2: version === 'v2',
    loading,
  };
}
